// Topbar — judul halaman + status koneksi + menu akun/tampilan (sheet).
// Status online/offline selalu terlihat (rfc-006 §6).

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Building2, LogOut, RefreshCw, Settings2, Wifi, WifiOff } from "lucide-react";
import {
  Badge,
  Button,
  SearchSelect,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from "@ds/ui";
import { useAuth } from "../../lib/auth";
import { useBranch } from "../../lib/branch";
import { useOnline } from "../../lib/useOnline";
import { findItemByPath } from "../../lib/nav";
import { flushOutbox, useOutboxCount } from "../../data/outbox";
import { AppearanceControls } from "./AppearanceControls";

const ROLE_LABEL: Record<string, string> = {
  owner: "Pemilik",
  branch_manager: "Manajer Cabang",
  cashier: "Kasir",
};

export function Topbar() {
  const online = useOnline();
  const { account, token, signOut } = useAuth();
  const branch = useBranch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const pending = useOutboxCount();
  const title = findItemByPath(location.pathname)?.label ?? "Reza POS";

  async function syncNow() {
    if (!token || syncing) return;
    setSyncing(true);
    try {
      const left = await flushOutbox(token);
      toast.success(left === 0 ? "Semua transaksi tersinkron." : `${left} transaksi masih menunggu.`);
    } finally {
      setSyncing(false);
    }
  }

  async function changeBranch(next: string) {
    if (!next || next === branch.activeId) return;
    setSwitching(true);
    try {
      await branch.switchTo(next);
      toast.success("Cabang aktif diganti.");
    } catch {
      toast.error("Gagal ganti cabang.");
    } finally {
      setSwitching(false);
    }
  }

  return (
    <header className="z-sticky sticky top-0 flex h-16 items-center justify-between gap-2 border-b border-border bg-bg/95 px-4 backdrop-blur">
      <h1 className="truncate text-lg font-semibold">{title}</h1>
      <div className="flex items-center gap-2">
        {branch.active ? (
          <Badge variant="neutral" title={branch.active.name}>
            <Building2 className="size-3" aria-hidden />
            {branch.active.code}
          </Badge>
        ) : null}
        <Badge variant={online ? "success" : "warn"}>
          {online ? <Wifi className="size-3" aria-hidden /> : <WifiOff className="size-3" aria-hidden />}
          {online ? "Online" : "Offline"}
        </Badge>
        {pending > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={syncNow}
            disabled={syncing || !online}
            title={online ? "Sinkronkan transaksi offline" : "Menunggu online untuk sinkron"}
          >
            <RefreshCw className={`size-3 ${syncing ? "animate-spin" : ""}`} aria-hidden />
            {pending} antrean
          </Button>
        ) : null}
        <Button size="icon" variant="ghost" onClick={() => setOpen(true)} aria-label="Akun & tampilan">
          <Settings2 className="size-5" />
        </Button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>Akun & tampilan</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-5">
            <div className="rounded-lg border border-border bg-surface p-3">
              <div className="text-sm text-muted">Masuk sebagai</div>
              <div className="text-lg font-semibold">{account?.name}</div>
              <Badge variant={account?.role === "owner" ? "primary" : "neutral"}>
                {ROLE_LABEL[account?.role ?? ""] ?? account?.role ?? "Kasir"}
              </Badge>
            </div>
            {branch.active || branch.canSwitch ? (
              <div>
                <div className="mb-2 text-sm font-medium">Cabang aktif</div>
                {branch.canSwitch ? (
                  <SearchSelect
                    value={branch.activeId}
                    onChange={changeBranch}
                    disabled={switching}
                    ariaLabel="Pilih cabang aktif"
                    placeholder="— pilih cabang —"
                    options={branch.options.map((b) => ({ value: b.id, label: b.name, hint: b.code }))}
                  />
                ) : (
                  <div className="rounded-lg border border-border bg-surface p-3 text-sm">
                    <span className="font-semibold">{branch.active?.name}</span>
                    <span className="ml-2 text-xs text-muted">{branch.active?.code}</span>
                  </div>
                )}
                <p className="mt-2 text-xs text-muted">
                  Stok, harga, shift & laporan mengikuti cabang aktif. Ganti cabang menarik ulang data.
                </p>
              </div>
            ) : null}
            <div>
              <div className="mb-2 text-sm font-medium">Tampilan</div>
              <AppearanceControls block />
              <p className="mt-2 text-xs text-muted">
                Tema & skala teks tersimpan untuk akun ini.
              </p>
            </div>
          </SheetBody>
          <SheetFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                signOut();
              }}
            >
              <LogOut className="size-4" aria-hidden />
              Keluar
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </header>
  );
}
